import { Fragment, useRef, useState } from "react";
import { useParams } from "react-router";
import { KARTENFORMATE, NOTFALLKONTAKTARTEN, REGELN } from "@dj/domain/revier";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Auswahl, Textfeld } from "../design/Formular";
import { Abweisung, Status } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument } from "../plattform/lesemodelle";
import { useReviername } from "./Revier";

/**
 * Stammdaten des Reviers (oberflaeche.md §7 „Reviere / Revier“, befehle.md §6.2): Revierregeln, Sicherheitsbestimmungen
 * (Vorlage), Notfallkontakte, Rettungspunkte und Revierkarte. Jede Drückjagd übernimmt die Vorlage bei ihrer Anlage
 * (Context Map „Revier → Drückjagd“). Gelesen werden die Lesemodelle der Jagdherren.
 */

const KEIN = "—";

type Grundsetzer = (grund: string | null) => void;

async function senden(name: string, daten: Record<string, unknown>, setGrund: Grundsetzer): Promise<boolean> {
  setGrund(null);
  const a = await befehl(name, daten);
  if (!a.ok) setGrund(abweisungsgrund(a));
  return a.ok;
}

function zahl(text: string): number | null {
  const t = text.trim().replace(",", ".");
  return t && Number.isFinite(Number(t)) ? Number(t) : null;
}

function useStammdaten(revierId: string) {
  const d = useDokument(`reviere/${revierId}/stammdaten/aktuell`);
  return {
    laedt: d.laedt,
    notfallkontakte: (d.daten?.["Notfallkontakte"] as Array<Record<string, unknown>> | undefined) ?? [],
    rettungspunkte: (d.daten?.["Rettungspunkte"] as Array<Record<string, unknown>> | undefined) ?? [],
    sicherheitsbestimmungen: (d.daten?.["Sicherheitsbestimmungen"] as string[] | undefined) ?? [],
  };
}

// ── Revierregeln ─────────────────────────────────────────────────────────────────────────────────────────────────

export function Revierregeln() {
  const { revierId = "" } = useParams();
  const name = useReviername(revierId);
  const regeln = useDokument(`reviere/${revierId}/revierregeln/aktuell`);
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [grund, setGrund] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  const wert = (regel: string) => entwurf[regel] ?? regelwert(regeln.daten, regel);
  const setzen = (regel: string) => (w: string) => { setGespeichert(false); setEntwurf((e) => ({ ...e, [regel]: w })); };

  async function speichern() {
    setGespeichert(false);
    if (await senden("RevierregelnAendern", { Revier: revierId, ...entwurf }, setGrund)) {
      setEntwurf({});
      setGespeichert(true);
    }
  }

  return (
    <Ansicht name="Revierregeln" symbol="einstellungen" unterzeile={name ?? undefined}>
      <div className="text-bereich platte formular">
        <Auswahl label="Ansprachform" optionen={["Du", "Sie"]} wert={wert("Ansprachform")} onChange={setzen("Ansprachform")} />
        <Textfeld label="Zugang nach Abschluss einer Drückjagd" wert={wert("Zugang nach Abschluss einer Drückjagd")}
          onChange={setzen("Zugang nach Abschluss einer Drückjagd")} hilfe="z. B. 30 Tage" />
        <Textfeld label="Anonymisierung personenbezogener Details" wert={wert("Anonymisierung personenbezogener Details")}
          onChange={setzen("Anonymisierung personenbezogener Details")} hilfe="z. B. Ende des Jagdjahres plus ein Jahr" />
        <Auswahl label="Erlegerregel bei Fangschuss" optionen={["Erstschütze", "Fangschütze"]} wert={wert("Erlegerregel bei Fangschuss")}
          onChange={setzen("Erlegerregel bei Fangschuss")} />
        <Abweisung grund={grund} />
        <Knopf onPress={speichern} isDisabled={Object.keys(entwurf).length === 0}>Speichern</Knopf>
        {gespeichert ? <Status symbol="haken">Gespeichert</Status> : null}
      </div>
    </Ansicht>
  );
}

function regelwert(daten: Record<string, unknown> | null | undefined, regel: string): string {
  return (REGELN as readonly string[]).includes(regel) && daten?.[regel] !== undefined ? String(daten[regel]) : "";
}

// ── Sicherheitsbestimmungen (Vorlage) ───────────────────────────────────────────────────────────────────────────

export function Sicherheitsbestimmungen() {
  const { revierId = "" } = useParams();
  const name = useReviername(revierId);
  const { sicherheitsbestimmungen: liste, laedt } = useStammdaten(revierId);
  const [dialog, setDialog] = useState<{ index: number | null; text: string } | null>(null);
  const [grund, setGrund] = useState<string | null>(null);

  async function festlegen(neu: string[]) {
    if (await senden("SicherheitsbestimmungenFestlegen", { Revier: revierId, Sicherheitsbestimmungen: neu }, setGrund)) setDialog(null);
  }

  return (
    <Ansicht name="Sicherheitsbestimmungen" symbol="einweisung" unterzeile={name ?? undefined} unten={<>
      <Abweisung grund={dialog ? null : grund} />
      <Knopf art="planung" symbol="hinzufuegen" onPress={() => setDialog({ index: null, text: "" })}>Hinzufügen</Knopf>
    </>}>
      <section className="liste-bereich platte" role="region" aria-label="Vorlage">
        <h2 className="kopf"><Symbol name="einweisung" />Vorlage für jede neue Drückjagd</h2>
        {liste.map((text, i) => (
          <article key={i} className="zeile" aria-label={text}>
            <div><div className="zeile__u">{i + 1}</div><div className="zeile__t">{text}</div></div>
            <div className="knopfreihe">
              <Knopf art="planung-zweit" symbol="aendern" onPress={() => setDialog({ index: i, text })}>Ändern</Knopf>
              <Knopf art="planung-zweit" symbol="schliessen" onPress={() => festlegen(liste.filter((_, j) => j !== i))}>Entfernen</Knopf>
            </div>
          </article>
        ))}
        {liste.length === 0 && !laedt ? <div className="leer"><Symbol name="einweisung" /><span>Keine Sicherheitsbestimmungen</span></div> : null}
      </section>
      <Dialog name={dialog?.index === null ? "Hinzufügen" : "Ändern"} symbol="einweisung" offen={!!dialog} onSchliessen={() => setDialog(null)}>
        {dialog ? (
          <div className="formular">
            <Textfeld label="Sicherheitsbestimmung" wert={dialog.text} onChange={(text) => setDialog({ ...dialog, text })} autoFocus />
            <Abweisung grund={grund} />
            <div className="knopfreihe">
              <Knopf onPress={() => festlegen(dialog.index === null ? [...liste, dialog.text] : liste.map((t, j) => (j === dialog.index ? dialog.text : t)))}>
                Speichern
              </Knopf>
              <Knopf art="zweit" onPress={() => setDialog(null)}>Abbrechen</Knopf>
            </div>
          </div>
        ) : null}
      </Dialog>
    </Ansicht>
  );
}

// ── Notfallkontakte (112 immer enthalten) ───────────────────────────────────────────────────────────────────────

interface Kontaktentwurf { bezeichnung: string | null; Art: string; Bezeichnung: string; Telefonnummer: string; Adresse: string }

export function Notfallkontakte() {
  const { revierId = "" } = useParams();
  const name = useReviername(revierId);
  const { notfallkontakte: liste, laedt } = useStammdaten(revierId);
  const [dialog, setDialog] = useState<Kontaktentwurf | null>(null);
  const [grund, setGrund] = useState<string | null>(null);

  async function speichern(k: Kontaktentwurf) {
    const adresse = k.Adresse.trim() || null;
    const ok = k.bezeichnung === null
      ? await senden("NotfallkontakteHinzufuegen", {
        Revier: revierId, Notfallkontakte: [{ Art: k.Art, Bezeichnung: k.Bezeichnung, Telefonnummer: k.Telefonnummer, ...(adresse ? { Adresse: adresse } : {}) }],
      }, setGrund)
      : await senden("NotfallkontaktAendern", { Revier: revierId, Notfallkontakt: k.bezeichnung, Telefonnummer: k.Telefonnummer, Adresse: adresse }, setGrund);
    if (ok) setDialog(null);
  }

  return (
    <Ansicht name="Notfallkontakte" symbol="notruf" unterzeile={name ?? undefined} unten={<>
      <Abweisung grund={dialog ? null : grund} />
      <Knopf art="planung" symbol="hinzufuegen"
        onPress={() => setDialog({ bezeichnung: null, Art: "Tierarzt", Bezeichnung: "", Telefonnummer: "", Adresse: "" })}>Hinzufügen</Knopf>
    </>}>
      <section className="liste-bereich platte" role="region" aria-label="Notfallkontakte des Reviers">
        <h2 className="kopf"><Symbol name="notruf" />Notfallkontakte</h2>
        <table className="tabelle">
          <thead><tr><th>Art</th><th>Bezeichnung</th><th>Telefonnummer</th><th>Adresse</th><th aria-label="Aktion" /></tr></thead>
          <tbody>
            {liste.map((k) => {
              const notruf = k["Art"] === "Notruf";
              return (
                <tr key={String(k["Bezeichnung"])}>
                  <td>{String(k["Art"])}</td>
                  <td>{String(k["Bezeichnung"])}</td>
                  <td>{String(k["Telefonnummer"])}</td>
                  <td>{k["Adresse"] ? String(k["Adresse"]) : KEIN}</td>
                  <td>
                    {notruf ? null : (
                      <div className="knopfreihe">
                        <Knopf art="planung-zweit" symbol="aendern" onPress={() => setDialog({
                          bezeichnung: String(k["Bezeichnung"]), Art: String(k["Art"]), Bezeichnung: String(k["Bezeichnung"]),
                          Telefonnummer: String(k["Telefonnummer"]), Adresse: k["Adresse"] ? String(k["Adresse"]) : "",
                        })}>Ändern</Knopf>
                        <Knopf art="planung-zweit" symbol="schliessen"
                          onPress={() => senden("NotfallkontaktEntfernen", { Revier: revierId, Notfallkontakt: k["Bezeichnung"] }, setGrund)}>Entfernen</Knopf>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {liste.length === 0 && !laedt ? <div className="leer"><Symbol name="notruf" /><span>Keine Notfallkontakte</span></div> : null}
        <p className="erkl">Der Notruf 112 ist immer enthalten und lässt sich weder ändern noch entfernen.</p>
      </section>
      <Dialog name={dialog?.bezeichnung === null ? "Hinzufügen" : "Ändern"} symbol="notruf" offen={!!dialog} onSchliessen={() => setDialog(null)}>
        {dialog ? (
          <div className="formular">
            {dialog.bezeichnung === null ? (
              <>
                <Auswahl label="Art" optionen={NOTFALLKONTAKTARTEN.filter((a) => a !== "Notruf")} wert={dialog.Art}
                  onChange={(Art) => setDialog({ ...dialog, Art })} />
                <Textfeld label="Bezeichnung" wert={dialog.Bezeichnung} onChange={(Bezeichnung) => setDialog({ ...dialog, Bezeichnung })} />
              </>
            ) : <p className="erkl">{dialog.Art} · {dialog.Bezeichnung}</p>}
            <Textfeld label="Telefonnummer" typ="tel" eingabemodus="tel" wert={dialog.Telefonnummer}
              onChange={(Telefonnummer) => setDialog({ ...dialog, Telefonnummer })} />
            <Textfeld label="Adresse" wert={dialog.Adresse} onChange={(Adresse) => setDialog({ ...dialog, Adresse })} />
            <Abweisung grund={grund} />
            <div className="knopfreihe">
              <Knopf onPress={() => speichern(dialog)}>Speichern</Knopf>
              <Knopf art="zweit" onPress={() => setDialog(null)}>Abbrechen</Knopf>
            </div>
          </div>
        ) : null}
      </Dialog>
    </Ansicht>
  );
}

// ── Rettungspunkte ──────────────────────────────────────────────────────────────────────────────────────────────

interface Rettungspunktentwurf { kennung: string | null; Kennung: string; Breite: string; "Länge": string; Lagebeschreibung: string }

function koordinate(wert: unknown): string {
  return typeof wert === "number" && Number.isFinite(wert) ? wert.toFixed(6) : "";
}

export function Rettungspunkte() {
  const { revierId = "" } = useParams();
  const name = useReviername(revierId);
  const { rettungspunkte: liste, laedt } = useStammdaten(revierId);
  const [dialog, setDialog] = useState<Rettungspunktentwurf | null>(null);
  const [grund, setGrund] = useState<string | null>(null);

  async function speichern(p: Rettungspunktentwurf) {
    const lage = p.Lagebeschreibung.trim() || null;
    const ok = p.kennung === null
      ? await senden("RettungspunkteHinzufuegen", {
        Revier: revierId, Rettungspunkte: [{ Kennung: p.Kennung, Breite: zahl(p.Breite), "Länge": zahl(p["Länge"]), ...(lage ? { Lagebeschreibung: lage } : {}) }],
      }, setGrund)
      : await senden("RettungspunktAendern", {
        Revier: revierId, Rettungspunkt: p.kennung, Breite: zahl(p.Breite), "Länge": zahl(p["Länge"]), Lagebeschreibung: lage,
      }, setGrund);
    if (ok) setDialog(null);
  }

  return (
    <Ansicht name="Rettungspunkte" symbol="rettungspunkt" unterzeile={name ?? undefined} unten={<>
      <Abweisung grund={dialog ? null : grund} />
      <Knopf art="planung" symbol="hinzufuegen"
        onPress={() => setDialog({ kennung: null, Kennung: "", Breite: "", "Länge": "", Lagebeschreibung: "" })}>Hinzufügen</Knopf>
    </>}>
      <section className="liste-bereich platte" role="region" aria-label="Rettungspunkte des Reviers">
        <h2 className="kopf"><Symbol name="rettungspunkt" />Rettungspunkte</h2>
        <table className="tabelle">
          <thead><tr><th>Kennung</th><th>Breite</th><th>Länge</th><th>Lagebeschreibung</th><th aria-label="Aktion" /></tr></thead>
          <tbody>
            {liste.map((p) => (
              <tr key={String(p["Kennung"])}>
                <td>{String(p["Kennung"])}</td>
                <td className="mono">{koordinate(p["Breite"])}</td>
                <td className="mono">{koordinate(p["Länge"])}</td>
                <td>{p["Lagebeschreibung"] ? String(p["Lagebeschreibung"]) : KEIN}</td>
                <td>
                  <div className="knopfreihe">
                    <Knopf art="planung-zweit" symbol="aendern" onPress={() => setDialog({
                      kennung: String(p["Kennung"]), Kennung: String(p["Kennung"]), Breite: koordinate(p["Breite"]), "Länge": koordinate(p["Länge"]),
                      Lagebeschreibung: p["Lagebeschreibung"] ? String(p["Lagebeschreibung"]) : "",
                    })}>Ändern</Knopf>
                    <Knopf art="planung-zweit" symbol="schliessen"
                      onPress={() => senden("RettungspunktEntfernen", { Revier: revierId, Rettungspunkt: p["Kennung"] }, setGrund)}>Entfernen</Knopf>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {liste.length === 0 && !laedt ? <div className="leer"><Symbol name="rettungspunkt" /><span>Keine Rettungspunkte</span></div> : null}
      </section>
      <Dialog name={dialog?.kennung === null ? "Hinzufügen" : "Ändern"} symbol="rettungspunkt" offen={!!dialog} onSchliessen={() => setDialog(null)}>
        {dialog ? (
          <div className="formular">
            {dialog.kennung === null
              ? <Textfeld label="Kennung" wert={dialog.Kennung} onChange={(Kennung) => setDialog({ ...dialog, Kennung })} mono />
              : <p className="erkl">{dialog.Kennung}</p>}
            <Textfeld label="Breite" wert={dialog.Breite} onChange={(Breite) => setDialog({ ...dialog, Breite })} eingabemodus="numeric" mono />
            <Textfeld label="Länge" wert={dialog["Länge"]} onChange={(l) => setDialog({ ...dialog, "Länge": l })} eingabemodus="numeric" mono />
            <Textfeld label="Lagebeschreibung" wert={dialog.Lagebeschreibung} onChange={(Lagebeschreibung) => setDialog({ ...dialog, Lagebeschreibung })} />
            <Abweisung grund={grund} />
            <div className="knopfreihe">
              <Knopf onPress={() => speichern(dialog)}>Speichern</Knopf>
              <Knopf art="zweit" onPress={() => setDialog(null)}>Abbrechen</Knopf>
            </div>
          </div>
        ) : null}
      </Dialog>
    </Ansicht>
  );
}

// ── Revierkarte ─────────────────────────────────────────────────────────────────────────────────────────────────

const INHALTSTYPEN: Record<string, string> = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg" };

/** Upload nach medien.md §4: Upload-Platz anfordern, Datei direkt in den Eingang laden, `RevierkarteHochladen`. */
async function karteHochladen(revierId: string, datei: File, setGrund: Grundsetzer): Promise<boolean> {
  const endung = datei.name.split(".").pop()?.toLowerCase() ?? "";
  const inhaltstyp = datei.type || INHALTSTYPEN[endung] || "application/octet-stream";
  setGrund(null);
  const platz = await befehl("MedienUploadAnfordern", {
    Zweck: "Revierkarte", Revier: revierId, Medienart: "Revierkarte", Inhaltstyp: inhaltstyp, "Größe": datei.size, Dateiname: datei.name,
  });
  if (!platz.ok) { setGrund(abweisungsgrund(platz)); return false; }
  const r = await fetch(String(platz.felder["uploadUrl"]), {
    method: "PUT", headers: (platz.felder["uploadHeader"] as Record<string, string> | undefined) ?? { "Content-Type": inhaltstyp }, body: datei,
  }).catch(() => null);
  if (!r?.ok) { setGrund("upload-fehlgeschlagen"); return false; }
  return senden("RevierkarteHochladen", { Revier: revierId, Datei: platz.kennungen["Upload"], Dateiname: datei.name }, setGrund);
}

export function Revierkarte() {
  const { revierId = "" } = useParams();
  const name = useReviername(revierId);
  const karte = useDokument(`reviere/${revierId}/revierkarte/aktuell`);
  const [hochladen, setHochladen] = useState(false);
  const [datei, setDatei] = useState<File | null>(null);
  const [geo, setGeo] = useState<Record<string, string> | null>(null);
  const [grund, setGrund] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const dateiwahl = useRef<HTMLInputElement>(null);
  const georeferenz = (karte.daten?.["Georeferenz"] as Array<Record<string, unknown>> | null | undefined) ?? null;

  async function speichernKarte() {
    if (!datei) return;
    setLaeuft(true);
    const ok = await karteHochladen(revierId, datei, setGrund);
    setLaeuft(false);
    if (ok) { setHochladen(false); setDatei(null); }
  }

  async function speichernGeo() {
    if (!geo) return;
    const ecken = ["Nordwest", "Südost"].map((ecke) => ({ Ecke: ecke, Breite: zahl(geo[`${ecke} Breite`] ?? ""), "Länge": zahl(geo[`${ecke} Länge`] ?? "") }));
    if (await senden("RevierkarteGeoreferenzieren", { Revier: revierId, Ecken: ecken }, setGrund)) setGeo(null);
  }

  return (
    <Ansicht name="Revierkarte" symbol="karte" unterzeile={name ?? undefined}>
      <section className="liste-bereich platte" role="region" aria-label="Revierkarte des Reviers">
        <h2 className="kopf"><Symbol name="karte" />{karte.daten?.["Datei"] ? String(karte.daten["Datei"]) : "Keine Revierkarte"}</h2>
        {karte.daten ? (
          <dl className="angaben">
            <dt>Format</dt><dd>{String(karte.daten["Format"] ?? KEIN)}</dd>
            {(georeferenz ?? []).map((e) => (
              <Fragment key={String(e["Ecke"])}>
                <dt>{String(e["Ecke"])}</dt><dd className="mono">{Number(e["Breite"]).toFixed(6)}, {Number(e["Länge"]).toFixed(6)}</dd>
              </Fragment>
            ))}
            {georeferenz ? null : <><dt>Georeferenz</dt><dd>{KEIN}</dd></>}
          </dl>
        ) : null}
        <p className="erkl">{`Formate: ${KARTENFORMATE.join(", ")}. Am Jagdtag steht die Karte auch ohne Netz bereit.`}</p>
      </section>
      <div className="knopfreihe">
        <Knopf art="planung" symbol="anhang" onPress={() => setHochladen(true)}>Hochladen</Knopf>
        <Knopf art="planung" symbol="standort" isDisabled={!karte.daten} onPress={() => setGeo(Object.fromEntries((georeferenz ?? []).flatMap((e) => [
          [`${String(e["Ecke"])} Breite`, Number(e["Breite"]).toFixed(6)], [`${String(e["Ecke"])} Länge`, Number(e["Länge"]).toFixed(6)]])))}>
          Georeferenzieren
        </Knopf>
      </div>
      <Abweisung grund={hochladen || geo ? null : grund} />
      <Dialog name="Hochladen" symbol="karte" offen={hochladen} onSchliessen={() => setHochladen(false)}>
        <div className="formular">
          <Knopf art="planung-zweit" symbol="anhang" onPress={() => dateiwahl.current?.click()}>Datei</Knopf>
          {datei ? <p className="erkl">{datei.name}</p> : null}
          <input ref={dateiwahl} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
            onChange={(e) => setDatei(e.target.files?.[0] ?? null)} />
          <Abweisung grund={grund} />
          <div className="knopfreihe">
            <Knopf onPress={speichernKarte} isDisabled={!datei || laeuft}>Speichern</Knopf>
            <Knopf art="zweit" onPress={() => setHochladen(false)}>Abbrechen</Knopf>
          </div>
        </div>
      </Dialog>
      <Dialog name="Georeferenzieren" symbol="standort" offen={!!geo} onSchliessen={() => setGeo(null)}>
        {geo ? (
          <div className="formular">
            {["Nordwest Breite", "Nordwest Länge", "Südost Breite", "Südost Länge"].map((feld) => (
              <Textfeld key={feld} label={feld} wert={geo[feld] ?? ""} onChange={(w) => setGeo({ ...geo, [feld]: w })} eingabemodus="numeric" mono />
            ))}
            <Abweisung grund={grund} />
            <div className="knopfreihe">
              <Knopf onPress={speichernGeo}>Speichern</Knopf>
              <Knopf art="zweit" onPress={() => setGeo(null)}>Abbrechen</Knopf>
            </div>
          </div>
        ) : null}
      </Dialog>
    </Ansicht>
  );
}
